/**
 * Centralized Image Storage Utility
 * Handles bidirectional sync between Characters and Illustrations
 * Includes soft-delete with 10-day retention
 */

// Storage keys
const KEYS = {
    characterImages: (bookId, charId) => `eskrybe-char-images-${charId}`,
    savedPics: (bookId) => `eskrybe-saved-pics-${bookId}`,
    genHistory: (bookId) => `eskrybe-gen-history-${bookId}`,
    deletedFiles: (bookId) => `eskrybe-deleted-${bookId}`,
    folders: (bookId) => `eskrybe-folders-${bookId}`,
};

// Get all character images for a book (aggregated from all characters)
export const getAllCharacterImages = (bookId, characters) => {
    const allImages = [];
    
    characters.forEach(char => {
        const storageKey = KEYS.characterImages(bookId, char.id);
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const data = JSON.parse(saved);
            const images = (data.images || []).map(img => ({
                ...img,
                characterId: char.id,
                characterName: char.name,
                folder: char.name, // Use character name as folder
                isDefault: data.defaultIndex !== undefined && 
                           data.images.findIndex(i => i.id === img.id) === data.defaultIndex
            }));
            allImages.push(...images);
        }
    });
    
    return allImages;
};

// Get images organized by folder (character name)
export const getImagesByFolder = (bookId, characters) => {
    const folders = {};
    
    characters.forEach(char => {
        const storageKey = KEYS.characterImages(bookId, char.id);
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            const data = JSON.parse(saved);
            if (data.images && data.images.length > 0) {
                folders[char.name] = {
                    characterId: char.id,
                    characterName: char.name,
                    images: data.images.map((img, idx) => ({
                        ...img,
                        characterId: char.id,
                        characterName: char.name,
                        isDefault: idx === (data.defaultIndex || 0)
                    })),
                    defaultIndex: data.defaultIndex || 0
                };
            }
        }
    });
    
    // Sort folders alphabetically
    const sortedFolders = {};
    Object.keys(folders).sort().forEach(key => {
        sortedFolders[key] = folders[key];
    });
    
    return sortedFolders;
};

// Save image to character (and sync to illustrations)
export const saveImageToCharacter = (bookId, characterId, characterName, image, setAsDefault = false) => {
    const storageKey = KEYS.characterImages(bookId, characterId);
    const saved = localStorage.getItem(storageKey);
    const data = saved ? JSON.parse(saved) : { images: [], defaultIndex: 0 };
    
    const newImage = {
        id: image.id || Date.now().toString(),
        url: image.url,
        title: image.title || `${characterName} - Image`,
        createdAt: image.createdAt || new Date().toISOString(),
        bookLocation: image.bookLocation || null, // { chapter: 'Chapter 3', page: 27 }
        position: image.position || null // e.g., 'Top right 1/3 of page'
    };
    
    // Check if image already exists
    const existingIndex = data.images.findIndex(img => img.id === newImage.id || img.url === newImage.url);
    if (existingIndex === -1) {
        data.images.push(newImage);
        if (setAsDefault) {
            data.defaultIndex = data.images.length - 1;
        }
    } else if (setAsDefault) {
        data.defaultIndex = existingIndex;
    }
    
    localStorage.setItem(storageKey, JSON.stringify(data));
    return newImage;
};

// Update image metadata (e.g., book location)
export const updateImageMetadata = (bookId, characterId, imageId, updates) => {
    const storageKey = KEYS.characterImages(bookId, characterId);
    const saved = localStorage.getItem(storageKey);
    if (!saved) return null;
    
    const data = JSON.parse(saved);
    const imageIndex = data.images.findIndex(img => img.id === imageId);
    if (imageIndex === -1) return null;
    
    data.images[imageIndex] = { ...data.images[imageIndex], ...updates };
    localStorage.setItem(storageKey, JSON.stringify(data));
    return data.images[imageIndex];
};

// Set default image for character
export const setDefaultImage = (bookId, characterId, imageIndex) => {
    const storageKey = KEYS.characterImages(bookId, characterId);
    const saved = localStorage.getItem(storageKey);
    if (!saved) return false;
    
    const data = JSON.parse(saved);
    data.defaultIndex = imageIndex;
    localStorage.setItem(storageKey, JSON.stringify(data));
    return true;
};

// Delete image (soft delete - moves to trash)
export const deleteImage = (bookId, characterId, imageId) => {
    const storageKey = KEYS.characterImages(bookId, characterId);
    const saved = localStorage.getItem(storageKey);
    if (!saved) return false;
    
    const data = JSON.parse(saved);
    const imageIndex = data.images.findIndex(img => img.id === imageId);
    if (imageIndex === -1) return false;
    
    const [deletedImage] = data.images.splice(imageIndex, 1);
    
    // Adjust default index if needed
    if (data.defaultIndex >= data.images.length) {
        data.defaultIndex = Math.max(0, data.images.length - 1);
    }
    
    localStorage.setItem(storageKey, JSON.stringify(data));
    
    // Move to trash
    moveToTrash(bookId, {
        ...deletedImage,
        characterId,
        deletedAt: new Date().toISOString()
    });
    
    return true;
};

// Move image to trash (soft delete)
export const moveToTrash = (bookId, image) => {
    const trashKey = KEYS.deletedFiles(bookId);
    const saved = localStorage.getItem(trashKey);
    const trash = saved ? JSON.parse(saved) : [];
    
    trash.push({
        ...image,
        deletedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString() // 10 days
    });
    
    localStorage.setItem(trashKey, JSON.stringify(trash));
};

// Get deleted files (trash)
export const getDeletedFiles = (bookId) => {
    const trashKey = KEYS.deletedFiles(bookId);
    const saved = localStorage.getItem(trashKey);
    if (!saved) return [];
    
    const trash = JSON.parse(saved);
    const now = new Date();
    
    // Filter out expired items and clean up
    const validTrash = trash.filter(item => new Date(item.expiresAt) > now);
    
    if (validTrash.length !== trash.length) {
        localStorage.setItem(trashKey, JSON.stringify(validTrash));
    }
    
    return validTrash;
};

// Restore image from trash
export const restoreFromTrash = (bookId, imageId, characters) => {
    const trashKey = KEYS.deletedFiles(bookId);
    const saved = localStorage.getItem(trashKey);
    if (!saved) return null;
    
    const trash = JSON.parse(saved);
    const imageIndex = trash.findIndex(img => img.id === imageId);
    if (imageIndex === -1) return null;
    
    const [restoredImage] = trash.splice(imageIndex, 1);
    localStorage.setItem(trashKey, JSON.stringify(trash));
    
    // Restore to character
    if (restoredImage.characterId) {
        const char = characters.find(c => c.id === restoredImage.characterId);
        if (char) {
            saveImageToCharacter(bookId, restoredImage.characterId, char.name, restoredImage);
        }
    }
    
    return restoredImage;
};

// Permanently delete from trash
export const permanentlyDelete = (bookId, imageId) => {
    const trashKey = KEYS.deletedFiles(bookId);
    const saved = localStorage.getItem(trashKey);
    if (!saved) return false;
    
    const trash = JSON.parse(saved);
    const filtered = trash.filter(img => img.id !== imageId);
    localStorage.setItem(trashKey, JSON.stringify(filtered));
    return true;
};

// Empty entire trash
export const emptyTrash = (bookId) => {
    const trashKey = KEYS.deletedFiles(bookId);
    localStorage.setItem(trashKey, JSON.stringify([]));
};

// Get character's default image
export const getCharacterDefaultImage = (bookId, characterId) => {
    const storageKey = KEYS.characterImages(bookId, characterId);
    const saved = localStorage.getItem(storageKey);
    if (!saved) return null;
    
    const data = JSON.parse(saved);
    if (!data.images || data.images.length === 0) return null;
    
    return data.images[data.defaultIndex || 0] || null;
};

// Get all images for a specific character
export const getCharacterImages = (bookId, characterId) => {
    const storageKey = KEYS.characterImages(bookId, characterId);
    const saved = localStorage.getItem(storageKey);
    if (!saved) return { images: [], defaultIndex: 0 };
    
    return JSON.parse(saved);
};

export default {
    getAllCharacterImages,
    getImagesByFolder,
    saveImageToCharacter,
    updateImageMetadata,
    setDefaultImage,
    deleteImage,
    moveToTrash,
    getDeletedFiles,
    restoreFromTrash,
    permanentlyDelete,
    emptyTrash,
    getCharacterDefaultImage,
    getCharacterImages
};
